/**
 * Feature: Job Scheduling & Crew Management
 * /features/scheduling
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { Calendar, Users, AlertTriangle, Smartphone, Cloud, Bell, ArrowRight } from 'lucide-react';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'How many crews can I manage?',
    answer:
      'Unlimited on Pro and Scale plans. Each crew member has a profile and calendar. Free plan supports up to 3 active crew members.',
  },
  {
    question: 'Can foremen see their own schedule without admin access?',
    answer:
      'Yes. Field users see only their assigned jobs and schedules, not your full CRM or financial data.',
  },
  {
    question: 'Does scheduling sync with Google Calendar?',
    answer:
      'Google Calendar integration is on our roadmap. Currently, foremen use the Rooflyzer mobile app for their daily schedule.',
  },
  {
    question: 'How does weather rescheduling work?',
    answer:
      'Mark jobs as weather-sensitive and enter the work zip code. Rooflyzer monitors the 7-day forecast and alerts you if rain or freezing temps are forecast for your install date.',
  },
  {
    question: 'Can I set recurring jobs for maintenance customers?',
    answer:
      'Yes. Rooflyzer supports recurring job templates for maintenance and inspection contracts.',
  },
];

export const SchedulingPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Job Scheduling & Crew Management',
    description:
      'Schedule roofing jobs and manage crews with a visual calendar. Avoid conflicts, optimize crew capacity, and keep every job on track from assignment to completion.',
    featureList: [
      'Visual job calendar',
      'Crew capacity management',
      'Conflict detection',
      'Mobile schedule access',
      'Weather-based rescheduling',
      'Automated job reminders',
    ],
    url: 'https://rooflyzer.pro/features/scheduling',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Job Scheduling & Crew Management Software"
        description="Schedule roofing jobs and manage crews with a visual calendar. Avoid conflicts, optimize crew capacity, and keep every job on track from assignment to completion."
        canonicalUrl="/features/scheduling"
        keywords="roofing scheduling software, roofing crew management, roofing calendar app, contractor job scheduling"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Job Scheduling', url: '/features/scheduling' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Calendar}>No More Scheduling Chaos</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Schedule Every Crew, Every Job —{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Without the Chaos
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              A visual job calendar that shows every crew, every job, and every conflict at a
              glance. Drag, drop, and reassign in seconds. Your foremen know their schedule before
              they leave for the day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Schedule Jobs Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/features/project-management">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See the Calendar
                </Button>
              </Link>
            </div>
          </section>

          {/* Product Screenshot */}
          <section className="mb-24">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              <img
                src="/product-frames/calendar.webp"
                alt="Rooflyzer crew calendar with week view, team calendars, and appointment scheduling"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute bottom-6 left-6 z-20">
                <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-sm text-white/90 border border-white/20">
                  Live product screenshot
                </span>
              </div>
            </div>
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Every Tool Your Scheduler Needs
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: Calendar,
                  title: 'Visual Job Calendar',
                  desc: 'See all active jobs, assigned crews, and scheduled dates on a color-coded calendar. Day, week, and month views.',
                },
                {
                  icon: Users,
                  title: 'Crew Capacity Management',
                  desc: 'Set crew sizes and see capacity utilization at a glance. Never overbook a team or leave one idle.',
                },
                {
                  icon: AlertTriangle,
                  title: 'Conflict Detection',
                  desc: 'Automatic alerts when two jobs are scheduled for the same crew on the same day. Resolve with one drag-and-drop.',
                },
                {
                  icon: Smartphone,
                  title: 'Mobile Schedule Access',
                  desc: "Field crews see their daily schedule on the app. No phone calls needed to confirm who's going where.",
                },
                {
                  icon: Cloud,
                  title: 'Weather-Based Rescheduling',
                  desc: 'Flag weather-sensitive jobs and get alerts when rain or extreme temps are forecast for installation days.',
                },
                {
                  icon: Bell,
                  title: 'Automated Job Reminders',
                  desc: 'Customers get automated appointment reminders 24 hours before crew arrival. Reduce no-shows and callbacks.',
                },
              ]}
            />
          </section>

          {/* Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Whiteboard or spreadsheet scheduling updated manually',
                    'Double-booking accidents discovered day-of',
                    "Crew doesn't know their schedule until morning call",
                    'Customers call to confirm — waste your office time',
                    'Weather cancellations cascade into week-long chaos',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Rooflyzer Way</h2>
                <FeatureChecklist
                  items={[
                    'Visual calendar with drag-and-drop job assignment',
                    'Automatic conflict detection before it becomes a problem',
                    'Crews see their daily schedule on the mobile app',
                    'Automated customer reminders reduce no-shows by 28%',
                    'Weather alerts let you reschedule proactively',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats
            title="Real Numbers"
            items={[
              { value: '0 double', label: 'Booking Conflicts', desc: 'With automatic detection' },
              {
                value: '2 hrs/week',
                label: 'Saved on Scheduling Calls',
                desc: 'Per crew member',
              },
              {
                value: '28% fewer',
                label: 'Customer No-Shows',
                desc: 'With automated reminders',
              },
              { value: '5 seconds', label: 'To Reassign a Job', desc: 'With drag-and-drop' },
            ]}
          />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* Bottom CTA */}
          <FeatureSignup
            title="Ready to End Scheduling Chaos?"
            buttonLabel="Schedule Jobs Free — No Credit Card"
          >
            One calendar. Every crew. Every job. No conflicts, no callbacks, no chaos.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default SchedulingPage;
